import React, {useState, useEffect, useRef} from 'react';
import {Box, TableContainer, Thead, Tbody, Tr, Th, Td, Table, TableCaption, Tfoot, IconButton, useDisclosure} from '@chakra-ui/react';
import LoadingWheel from '../../components/General/LoadingWheel';
import {get, postDelete} from '../../service/ApiService';
import {User} from '../../service/Urls';
import {FaTrashAlt, FaEdit, FaUserPlus} from 'react-icons/fa';
import {useNavigate} from 'react-router-dom';
import ModalYesNo from '../../components/General/ModalYesNo';
import {setGrid} from '../../helpers/SetGrid';
import ErrorHelper from '../../service/ErrorHelper';
import {useTitle} from '../../components/General/Layout';

const UserMaintenancePage = props => {
  const [userData, setUserData] = useState([]);
  const [toast, setToast] = useState({});
  const [forDelete, setForDelete] = useState({id: '', userName: ''});
  const [elements, setElements] = useState({body: {}, header: {}, footer: {}});
  const [isLoading, setLoading] = useState(true);
  const [title, setTitle] = useTitle('Opportunity Plan Maintenance');
  const [pages, setPages] = useState({
    pageNum: 1,
    totalPages: 5,
    itemsPerPage: 10,
    getNewPage: curPage => {
      loadData(curPage);
    },
  });
  const navigate = useNavigate();
  const {isOpen, onOpen, onClose} = useDisclosure();

  // useRef will save this across renders so it's not reloaded every time
  // to add more fields to the table, simply add their name and type to this json
  // if a new type is added you'll have to update the table to reflect that type (ie. dropdown or button)
  const visibleData = useRef({firstName: 'text', lastName: 'text', userName: 'text', email: 'text', isActive: 'boolean', emailConfirmed: 'boolean'});

  // Initial load of the page
  useEffect(() => {
    loadData(1);
  }, []);

  const loadData = async curPage => {
    let data = '';
    data = await get(User.LIST + `?Page=${curPage}&PageSize=${pages.itemsPerPage}`);
    setToast(data);
    if (!data.error) {
      setUserData(data.data);
      // updates just one variable while keeping the state of the old ones
      setPages(prevState => ({
        ...prevState,
        totalPages: data.pageCount,
        pageNum: curPage,
      }));
      setLoading(false);
      setTitle('User Maintenance');
    }
  };

  // Need this to repopulate everything on the UI any time the page number or data from the web changes
  useEffect(() => {
    populateData();
  }, [userData, pages]);

  // if no userId is passed in, we'll assume we're adding.
  const addEdit = (userId = null) => {
    const id = userId ? userId : null;
    navigate('add-edit', {state: {id: id}});
  };

  const setForDeletion = id => {
    const userName = userData.filter(user => user.id == id);
    setForDelete({id: id, userName: userName[0].userName});
    onOpen();
  };

  const deleteUser = async () => {
    const data = await postDelete(User.DELETE, `"${forDelete.id}"`);
    setToast({response: data, successMessage: 'SuccessFully deleted ' + forDelete.userName, onSuccess: () => loadData(pages.pageNum)});
    onClose();
  };

  const populateData = () => {
    // for the function parameters passed in we're going to use the id of data being populated, can do other stuff with it but that was best solution I could come up with
    // this id will be set in the setGrid when the data is populated... really hoping all of our data will have id's!

    const ExtraColumns = ({id}) => {
      return (
        <>
          <Td>
            <IconButton colorScheme="tertiary" title="Edit" size="md" icon={<FaEdit />} mr="2" onClick={() => addEdit(id)} />
            <IconButton colorScheme="tertiary" title="Delete" size="md" icon={<FaTrashAlt />} onClick={() => setForDeletion(id)} />
          </Td>
        </>
      );
    };

    setGrid(visibleData, userData, pages, setElements, ExtraColumns);
  };

  return (
    <>
      {isLoading ? (
        <LoadingWheel />
      ) : (
        <Box width="95%">
          <ModalYesNo
            isOpen={isOpen}
            onOpen={onOpen}
            onClose={onClose}
            title="Delete user"
            body={'Are you sure you want to delete ' + forDelete.userName}
            yesFunction={deleteUser}
          />
          <TableContainer>
            <Table border="solid 2px #0c5f78" variant="striped" colorScheme="mint">
              <TableCaption>User Maintenance</TableCaption>
              <Thead>
                <Tr border="solid 2px #0c5f78">
                  {elements.header}
                  <Th>
                    <IconButton icon={<FaUserPlus />} variant="outline" colorScheme={'secondary'} title="Add User" onClick={() => addEdit()} />
                  </Th>
                </Tr>
              </Thead>
              <Tbody>{elements.body}</Tbody>
              <Tfoot>
                <Tr>{elements.footer}</Tr>
              </Tfoot>
            </Table>
          </TableContainer>
          <ErrorHelper toastResponse={toast} />
        </Box>
      )}
    </>
  );
};

export default UserMaintenancePage;
